/**
 * Theme hook.
 *
 * The stored preference is one of `System | Light | Dark` (the same union the account profile uses), and the applied
 * scheme is resolved from it plus `prefers-color-scheme`. That separation matters: choosing "System" must keep
 * following the operating system *after* the choice is made, which a resolved boolean could not express.
 */

import { useCallback, useEffect } from 'react';

import { useMediaQuery } from '@/hooks/useMediaQuery';
import { resolveTheme, type ResolvedTheme, useUiStore } from '@/stores/uiStore';
import type { ThemePreference } from '@/types/api';

/** Value returned by {@link useTheme}. */
export interface UseThemeResult {
  /** Stored preference. */
  preference: ThemePreference;
  /** Scheme currently applied to the document. */
  resolved: ResolvedTheme;
  /** Whether the operating system reports a dark appearance. */
  systemPrefersDark: boolean;
  /** Stores a new preference. */
  setPreference: (next: ThemePreference) => void;
}

/** Reads and changes the colour-scheme preference. */
export function useTheme(): UseThemeResult {
  const preference = useUiStore((state) => state.theme);
  const setPreference = useUiStore((state) => state.setTheme);
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  return {
    preference,
    resolved: resolveTheme(preference, systemPrefersDark),
    systemPrefersDark,
    setPreference,
  };
}

/**
 * Applies the resolved scheme to the document element.
 *
 * The `dark` class is toggled on `<html>`, which is where the CSS variables of `styles/theme.css` are redefined —
 * so a single class swap re-themes every component without React re-rendering subtrees.
 */
export function useAppliedTheme(): ResolvedTheme {
  const { resolved } = useTheme();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
    document.documentElement.style.colorScheme = resolved;
  }, [resolved]);

  return resolved;
}

/** Cycles through the three preferences; used by the topbar toggle. */
export function useCycledTheme(): () => void {
  const { preference, systemPrefersDark, setPreference } = useTheme();

  return useCallback(() => {
    // The cycle follows what the user sees: System → the opposite of the current appearance → System again.
    const effective = resolveTheme(preference, systemPrefersDark);
    setPreference(effective === 'dark' ? 'Light' : 'Dark');
  }, [preference, systemPrefersDark, setPreference]);
}
